/**
 * F-135 × F-27: the download button in the selection toolbar (docs/08 §2 —
 * an element type's folder owns its toolbar entry, so the toolbar never
 * special-cases a type). It shows for EVERY element that carries a file — a
 * `file` element and an image alike — which is why it is registered here,
 * beside the one definition of "what does this element download" (./download).
 *
 * Selecting the element puts the button in the pill; one click saves the
 * file under its uploaded name. Not gated on the board being editable:
 * saving a copy is a read, and F-135 promises it to everyone who can open
 * the board (the pill itself hides only for the F-45 offline freeze, where
 * the bytes may be unreachable anyway).
 *
 * `visible`, not `enabled`: a link card or a stroke has no file, and a greyed
 * button would only send people hunting for the setting that turns it on —
 * the registry's own rule for choosing between the two.
 */
import { iconDownload } from "../../../ui/icons.js";
import { registerToolbarAction, type ToolbarActionContext } from "../../toolbar-actions/index.js";
import { downloadElementAsset, downloadTarget } from "./download.js";

/**
 * The element the current selection would download, re-read from the doc on
 * every render AND at click time rather than captured once: the pill sits
 * open across remote edits, so an element deleted meanwhile must be judged
 * as it is NOW (C4-style id re-check).
 */
function selectedDownloadable(ctx: ToolbarActionContext) {
  const sel = ctx.selection;
  if (sel.kind !== "element") return null;
  const element = ctx.session.board.getElement(sel.elementId);
  if (element === undefined || downloadTarget(element) === null) return null;
  return element;
}

registerToolbarAction({
  id: "asset-download",
  appliesTo: "elements",
  tooltip: "Download",
  // After a link card's open button (5) and before bring-to-front (10): for
  // an image or a file, saving it is the first thing anyone reaches for.
  order: 6,
  icon: iconDownload,
  visible: (ctx) => selectedDownloadable(ctx) !== null,
  run: (ctx) => {
    const element = selectedDownloadable(ctx);
    if (element !== null) void downloadElementAsset(element);
  },
});
